import gql from 'graphql-tag';

import constants from './constants';
import { handlePaylineError, isPaylineSuccess } from './errors';
import { setActiveWidgetUrl } from './widgetEnvironment';

const handleResponse = (response) => {
  if (!isPaylineSuccess(response)) {
    throw handlePaylineError(null, response);
  }
  return response;
};

export const getBrand = (client, variables, fetchPolicy = 'no-cache') => client
    .query({
      query: gql`
        ${constants.gql.GET_BRAND}
      `,
      fetchPolicy,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineGetBrand : undefined;
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const capture = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.CAPTURE}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineCapture : undefined;
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const captureSubscriber = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.CAPTURE_SUBSCRIBER}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineCaptureSubscriber : undefined;
      // When 3DS challenge is required, return the response with 3DS data
      // (resultCode 01131 is not a fatal error - the frontend will handle the challenge)
      if (response && response.needs3DS) {
        return response;
      }
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const captureSubscriberAfter3DS = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.CAPTURE_SUBSCRIBER_AFTER_3DS}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineCaptureSubscriberAfter3DS : undefined;
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const createSubscriber = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.SUBSCRIBE}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineSubscribe : undefined;
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const subscribeAndCapture = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.SUBSCRIBE_AND_CAPTURE}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineSubscribeAndCapture : undefined;
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const captureWithToken = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.CAPTURE_WITH_TOKEN}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineCaptureWithToken : undefined;
      return handleResponse(response);
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const getToken = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.GET_TOKEN}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineGetToken : undefined;
      if (!response || !response.token) {
        throw new Error('Token de paiement introuvable.');
      }
      // Le serveur ne renseigne `widgetUrl` que pour un golf sorti de la
      // production : ailleurs il vaut null et l'application garde son build.
      setActiveWidgetUrl(response.widgetUrl);
      return response;
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

export const checkPayment = (client, variables) => client
    .query({
      query: gql`
        ${constants.gql.CHECK_PAYMENT}
      `,
      fetchPolicy: 'no-cache',
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineCheckPayment : undefined;
      if (!response) {
        throw new Error('Impossible de vérifier le paiement.');
      }
      return response;
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });

// Widget hébergé "nouvelle carte" : vérifie ET enregistre côté serveur le paiement
// encaissé, lié à la commande (orderId), avant bookingConfirm. Renvoie le même résultat
// que checkPayment (le client construit sa transaction et confirme comme avant), mais si
// la réponse se perd le paiement reste récupérable/remboursable (cf. incident LAZZARETTO).
export const recordWidgetPayment = (client, variables) => client
    .mutate({
      mutation: gql`
        ${constants.gql.RECORD_WIDGET_PAYMENT}
      `,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineRecordWidgetPayment : undefined;
      if (!response) {
        throw new Error('Impossible de vérifier le paiement.');
      }
      return response;
    })
    .catch((error) => {
      throw handlePaylineError(error);
    });
